import React, { useEffect, useState } from 'react';
import { ExclamationCircleOutlined, FileImageOutlined, FileTextOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, message, Modal, Upload, Tag } from 'antd';
import { each, isEmpty } from 'lodash';
import axios from 'axios';
import ErrorBoundary from 'components/Errors/ErrorBoundary';
import { DISPATCH_URL, UPLOAD_URL } from 'constants/ApiConstants';
import ModalCloseIcon from 'components/UtilComponents/ModalCloseIcon';

const UploadDispatchAttachments = ({ visible, close, selected }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible]);

  const getFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const onSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const values = form.getFieldsValue(true);

      const files = new FormData();

      if (!isEmpty(values?.invoice)) {
        files.append('purchase.invoice', values?.invoice[0].originFileObj);
        if (values?.purchaseInvoiceTime) {
          files.append('purchase.invoice_time', values?.purchaseInvoiceTime);
        }
      }
      if (!isEmpty(values?.ewayBill)) {
        files.append('purchase.ewayBill', values?.ewayBill[0].originFileObj);
        if (values?.purchaseEwayBillTime) {
          files.append('purchase.ewayBill_time', values?.purchaseEwayBillTime);
        }
      }
      if (!isEmpty(values?.weightSlip)) {
        files.append('purchase.weightSlip', values?.weightSlip[0].originFileObj);
        if (values?.purchaseWeightSlipTime) {
          files.append('purchase.weightSlip_time', values?.purchaseWeightSlipTime);
        }
      }
      if (!isEmpty(values?.qualityReport)) {
        files.append('purchase.qualityReport', values?.qualityReport[0].originFileObj);
        if (values?.purchaseQualityReportTime) {
          files.append('purchase.qualityReport_time', values?.purchaseQualityReportTime);
        }
      }
      if (!isEmpty(values?.gstReceipt)) {
        files.append('purchase.gstReceipt', values?.gstReceipt[0].originFileObj);
        if (values?.purchaseGstReceiptTime) {
          files.append('purchase.gstReceipt_time', values?.purchaseGstReceiptTime);
        }
      }
      if (!isEmpty(values?.attachments)) {
        each(values.attachments, (attachment) => {
          files.append('purchase.attachments', attachment.originFileObj);
        });
        if (values?.purchaseAttachmentsTime) {
          files.append('purchase.attachments_time', values?.purchaseAttachmentsTime);
        }
      }

      const res = await axios.post(`${DISPATCH_URL}/upload/${selected?._id}`, files);
      if (res.data.status === 'success') {
        message.success('Uploaded Dispatch attachments successfully');
        form.resetFields();
        close(true);
      } else {
        message.error(res.data.error);
      }
    } catch (err) {
      message.error(err.message);
    }
    setLoading(false);
  };

  const confirmOnClose = () => {
    if (!loading && form.isFieldsTouched()) {
      Modal.confirm({
        title: 'Unsaved Data',
        icon: <ExclamationCircleOutlined />,
        content: 'This form has unsaved data. Are you sure you want to close the screen?',
        okText: 'Yes',
        onOk: () => {
          form.resetFields();
          close(false);
        },
        cancelText: 'No',
      });
    } else {
      close(false);
    }
  };

  return (
    <Modal
      title="Upload Dispatch Attachments"
      open={visible}
      onCancel={confirmOnClose}
      footer={null}
      closeIcon={<ModalCloseIcon />}
      maskClosable={false}
      width={480}
      bodyStyle={{ height: '75vh', overflow: 'scroll' }}>
      <ErrorBoundary>
        <Form form={form} layout="vertical" onFinish={onSubmit} requiredMark={false} scrollToFirstError={true}>
          <Form.Item name="invoice" label="Invoice" getValueFromEvent={getFile}>
            <Upload maxCount={1} beforeUpload={() => false} defaultFileList={form.getFieldValue('invoice') || []} accept=".jpg, .jpeg, .png, .pdf">
              <Button style={{ width: '325px' }} size="large" icon={<FileImageOutlined />}>
                Upload Invoice
              </Button>
            </Upload>
          </Form.Item>
          {selected?.purchase?.invoice && (
            <Button href={`${UPLOAD_URL}/${selected.purchase.invoice}`} type="link" target="_blank" style={{ marginTop: -24, marginBottom: 10 }}>
              View Existing Invoice
            </Button>
          )}
          <Form.Item name="purchaseInvoiceTime">
            <DatePicker size="large" placeholder="Select Invoice Date" showTime allowClear={false} format="DD/MM/yyyy hh:mm A" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="ewayBill" label="E-Way Bill" getValueFromEvent={getFile}>
            <Upload maxCount={1} beforeUpload={() => false} defaultFileList={form.getFieldValue('ewayBill') || []} accept=".jpg, .jpeg, .png, .pdf">
              <Button style={{ width: '325px' }} size="large" icon={<FileImageOutlined />}>
                Upload Eway Bill
              </Button>
            </Upload>
          </Form.Item>
          {selected?.purchase?.ewayBill && (
            <Button href={`${UPLOAD_URL}/${selected.purchase.ewayBill}`} type="link" target="_blank" style={{ marginTop: -24, marginBottom: 10 }}>
              View Existing E-Way Bill / GRN Receipt
            </Button>
          )}
          <Form.Item name="purchaseEwayBillTime">
            <DatePicker size="large" placeholder="Select E-Way Bill Date" showTime allowClear={false} format="DD/MM/yyyy hh:mm A" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="weightSlip" label="Weight Slip" getValueFromEvent={getFile}>
            <Upload maxCount={1} beforeUpload={() => false} defaultFileList={form.getFieldValue('weightSlip') || []} accept=".jpg, .jpeg, .png, .pdf">
              <Button style={{ width: '325px' }} size="large" icon={<FileTextOutlined />}>
                Upload Weight Slip
              </Button>
            </Upload>
          </Form.Item>
          {selected?.purchase?.weightSlip && (
            <Button href={`${UPLOAD_URL}/${selected.purchase.weightSlip}`} type="link" target="_blank" style={{ marginTop: -24, marginBottom: 10 }}>
              View Existing Weight Slip
            </Button>
          )}
          <Form.Item name="purchaseWeightSlipTime">
            <DatePicker size="large" placeholder="Select Weight Slip Date" showTime allowClear={false} format="DD/MM/yyyy hh:mm A" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="qualityReport" label="Quality Report" getValueFromEvent={getFile}>
            <Upload maxCount={1} beforeUpload={() => false} defaultFileList={form.getFieldValue('qualityReport') || []} accept=".jpg, .jpeg, .png, .pdf">
              <Button style={{ width: '325px' }} size="large" icon={<FileTextOutlined />}>
                Upload Quality Report
              </Button>
            </Upload>
          </Form.Item>
          {selected?.purchase?.qualityReport && (
            <Button href={`${UPLOAD_URL}/${selected.purchase.qualityReport}`} type="link" target="_blank" style={{ marginTop: -24, marginBottom: 10 }}>
              View Existing Quality Report
            </Button>
          )}
          <Form.Item name="purchaseQualityReportTime">
            <DatePicker size="large" placeholder="Select Quality Report Date" showTime allowClear={false} format="DD/MM/yyyy hh:mm A" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="gstReceipt" label="GST Receipt" getValueFromEvent={getFile}>
            <Upload maxCount={1} beforeUpload={() => false} defaultFileList={form.getFieldValue('gstReceipt') || []} accept=".jpg, .jpeg, .png, .pdf">
              <Button style={{ width: '325px' }} size="large" icon={<FileTextOutlined />}>
                Upload GST Receipt
              </Button>
            </Upload>
          </Form.Item>
          {selected?.purchase?.gstReceipt && (
            <Button href={`${UPLOAD_URL}/${selected.purchase.gstReceipt}`} type="link" target="_blank" style={{ marginTop: -24, marginBottom: 10 }}>
              View Existing GST Receipt
            </Button>
          )}
          <Form.Item name="purchaseGstReceiptTime">
            <DatePicker size="large" placeholder="Select GST Receipt Date" showTime allowClear={false} format="DD/MM/yyyy hh:mm A" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="attachments" label="Attachments" getValueFromEvent={getFile}>
            <Upload multiple beforeUpload={() => false} defaultFileList={form.getFieldValue('attachments') || []} accept=".jpg, .jpeg, .png, .pdf">
              <Button style={{ width: '325px' }} size="large" icon={<FileTextOutlined />}>
                Upload Additional Files
              </Button>
            </Upload>
          </Form.Item>
          {selected?.purchase?.attachments && (
            <div style={{ marginTop: -14, marginBottom: 10 }}>
              {selected?.purchase.attachments.map((attachment, index) => (
                <Button href={`${UPLOAD_URL}/${attachment}`} type="link" target="_blank">
                  View Existing Attachment {index + 1}
                </Button>
              ))}
              <Tag color="red" style={{ marginTop: 5 }}>
                * Existing attachments will be replaced if you upload new ones
              </Tag>
            </div>
          )}
          <Form.Item name="purchaseAttachmentsTime">
            <DatePicker size="large" placeholder="Select Attachments Date" showTime allowClear={false} format="DD/MM/yyyy hh:mm A" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item className="mt-4">
            <Button type="primary" size="large" block htmlType="submit" loading={loading}>
              Upload Dispatch Attachments
            </Button>
          </Form.Item>
        </Form>
      </ErrorBoundary>
    </Modal>
  );
};

export default UploadDispatchAttachments;
